// MinimalTaskDetails.jsx - A minimal, progressive disclosure task detail component
import React, { useState, useCallback, useMemo, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Collapse,
  Chip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Skeleton,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  Select,
  FormControl,
  Avatar,
  AvatarGroup,
  Tooltip,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { debounce } from 'lodash';
import {
  Check as CheckIcon,
  Circle as TodoIcon,
  Schedule as InProgressIcon,
  RateReview as InReviewIcon,
  ExpandMore as ExpandIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Flag as PriorityIcon,
  Add as AddIcon,
  Close as CloseIcon,
  ArrowBack as BackIcon,
  Folder as SectionIcon,
  Category as TypeIcon,
  Milestone as MilestoneIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Tag as TagIcon,
  Language as LanguageIcon,
  Share as ShareIcon,
  AttachFile as AttachFileIcon,
  Chat as ChatIcon,
  History as HistoryIcon,
  AccountTree as ProjectIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Import components
import InlineEditField from './components/InlineEditField';
import InlineEditFieldWithMentions from './components/InlineEditFieldWithMentions';
import TaskDescriptionField from './components/TaskDescriptionField'; // Enhanced description field
import TaskMetaBar from './components/TaskMetaBar';
import TaskItems from './components/TaskItems';
import MinimalComments from './components/MinimalComments';
import MinimalCommentsEnhanced from './components/MinimalCommentsEnhanced';
import MinimalContentFields from './components/MinimalContentFields';
import CollapsibleSection from './components/CollapsibleSection';
import TaskActivityFeed from './components/TaskActivityFeed';
import TaskActivityCompact from './components/TaskActivityCompact';
import TaskSubtaskList from './task-detail/TaskSubtaskList';
import AttachmentSection from '../attachments/AttachmentSection';
import AttachmentSectionEnhanced from '../attachments/AttachmentSectionEnhanced';
import PlatformSelector from './components/PlatformSelector';
import TaskTimeTracker from './components/TaskTimeTracker';
import TaskCollaborators from './components/TaskCollaborators';
import { useDebouncedBackgroundSave } from '../../hooks/useDebouncedBackgroundSave';

// Import platform options and category helper
import { PLATFORM_OPTIONS, getDefaultCategoriesForType } from '../features/content/form-utils/contentFormData';

// Import hooks and stores
import useTaskStore from '../../stores/taskStore';
import useProjectStore from '../../stores/projectStore';
import useAuthStore from '../../stores/authStore';
import useNotificationStore from '../../stores/notificationStore';
import useContentStore from '../../stores/contentStore';
import { useSocketEvent, useSocketConnection } from '../../hooks/useSocketEvent';
import { useSocket } from '../../contexts/SocketContext';
import { useTaskUrlSync } from '../../hooks/useTaskUrlSync';
import { format } from 'date-fns';
import * as taskService from '../../services/taskService';
import * as timeTrackingService from '../../services/timeTrackingService';

// Status configuration
const STATUS_CONFIG = {
  to_do: { icon: TodoIcon, label: 'To Do', color: 'default' },
  in_progress: { icon: InProgressIcon, label: 'In Progress', color: 'primary' },
  in_review: { icon: InReviewIcon, label: 'In Review', color: 'secondary' },
  done: { icon: CheckIcon, label: 'Done', color: 'success' },
};

// Task types
const TASK_TYPES = [
  { value: 'general', label: 'General' },
  { value: 'design', label: 'Design' },
  { value: 'development', label: 'Development' },
  { value: 'content', label: 'Content' },
  { value: 'review', label: 'Review' },
  { value: 'administrative', label: 'Administrative' },
  { value: 'other', label: 'Other' }
];

// Content stages
const CONTENT_STAGES = [
  { value: 'todo', label: 'To Do' },
  { value: 'idea', label: 'Idea' },
  { value: 'reference', label: 'Reference' },
  { value: 'creative', label: 'Creative' },
  { value: 'posting', label: 'Posting' },
  { value: 'completed', label: 'Completed' }
];

// Content types - must match backend enum values
const CONTENT_TYPES = [
  { value: 'post', label: 'Post' },
  { value: 'story', label: 'Story' },
  { value: 'reel', label: 'Reel' },
  { value: 'video', label: 'Video' },
  { value: 'article', label: 'Article' },
  { value: 'carousel', label: 'Carousel' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'event', label: 'Event' },
  { value: 'poll', label: 'Poll' },
  { value: 'contest', label: 'Contest' },
  { value: 'other', label: 'Other' }
];

// Language options - must match backend enum values
const LANGUAGE_OPTIONS = [
  { value: 'bilingual', label: 'Bilingual' },
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'Arabic' }
];

// Dynamic content categories based on content type
const getDynamicCategoriesForType = (contentType) => {
  const categories = getDefaultCategoriesForType(contentType);
  return categories.map(category => ({
    value: category,
    label: category
  }));
};

/**
 * MinimalTaskDetails - Progressive disclosure task detail component
 * Single source of truth for create/edit/view task operations
 */
const MinimalTaskDetails = ({
  taskId: propTaskId,
  isCreateMode = false,
  initialValues = {},
  onClose,
  onSubmit,
  embedded = false,
  hideProjectField = false,
  hideSectionField = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  // Stores
  const currentTask = useTaskStore(state => state.currentTask);
  const taskCreateContext = useTaskStore(state => state.taskCreateContext);
  const fetchTask = useTaskStore(state => state.fetchTask);
  const isTaskDrawerOpen = useTaskStore(state => state.isTaskDrawerOpen);
  
  // Use propTaskId if provided, otherwise use currentTask id
  const taskId = propTaskId || currentTask?._id;
  
  
  const updateTask = useTaskStore(state => state.updateTask);
  const patchTask = useTaskStore(state => state.patchTask);
  const createTask = useTaskStore(state => state.createTask);
  const deleteTask = useTaskStore(state => state.deleteTask);
  const error = useTaskStore(state => state.error);
  const projectSections = useTaskStore(state => state.projectSections);
  const fetchProjectSections = useTaskStore(state => state.fetchProjectSections);
  
  const { projects, currentProject, fetchProjects, fetchProject } = useProjectStore();
  const { user } = useAuthStore();
  const { showSuccess, showError } = useNotificationStore();
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'to_do',
    priority: 'medium',
    assignedTo: null,
    dueDate: null,
    projectId: currentProject?._id || '',
    sectionId: '',
    type: 'general',
    estimatedHours: '',
    actualHours: '',
    isClientVisible: false,
    milestoneId: '',
    contentStage: 'todo',
    contentType: 'post',
    category: '',
    language: 'bilingual',
    hashtags: [],
    platforms: [],
    ...initialValues,
  });
  
  // UI state
  const [expandedSections, setExpandedSections] = useState({
    details: false,
    content: false,
    timeTracking: false,
    collaboratorsDetail: false,  // For expanded collaborator view
    subtasks: false,
    checklist: false,  // Initially collapsed
  });
  
  // Tab state for Attachments, Discussion, and Activity
  const [activeTab, setActiveTab] = useState(0);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [deleteMenuAnchor, setDeleteMenuAnchor] = useState(null);
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  const [collaboratorMenuAnchor, setCollaboratorMenuAnchor] = useState(null);
  const [editingCollaborator, setEditingCollaborator] = useState(null);
  const [collaboratorActionMenu, setCollaboratorActionMenu] = useState({ anchor: null, collaborator: null });
  const [removeCollaboratorConfirm, setRemoveCollaboratorConfirm] = useState(null);
  
  // Content field anchors for inline editing
  const [contentTypeAnchor, setContentTypeAnchor] = useState(null);
  const [categoryAnchor, setCategoryAnchor] = useState(null);
  const [languageAnchor, setLanguageAnchor] = useState(null);
  const [hashtagsAnchor, setHashtagsAnchor] = useState(null);
  const [platformsAnchor, setPlatformsAnchor] = useState(null);
  const [stageAnchor, setStageAnchor] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [timeTrackerDialog, setTimeTrackerDialog] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  
  // Track if we're currently editing to prevent overwriting user changes
  const [isEditing, setIsEditing] = useState(false);
  const editingTimeoutRef = useRef(null);
  
  // Track last update timestamp to detect changes
  const lastUpdateRef = useRef(null);
  const [lastSyncTime, setLastSyncTime] = useState(0);
  
  // Share functionality
  const { copyTaskLink } = useTaskUrlSync();
  
  // Real-time updates hook
  const refreshTask = useCallback(async () => {
    if (taskId && !isCreateMode) {
      console.log('[MinimalTaskDetails] Refreshing task due to real-time update:', taskId);
      // Mark that we're expecting an update from real-time sync
      lastUpdateRef.current = Date.now();
      setIsSyncing(true);
      
      try {
        // Force a fresh fetch of the task
        const task = await fetchTask(taskId);
        console.log('[MinimalTaskDetails] Task refreshed:', {
          taskId: task?._id,
          title: task?.title,
          status: task?.status,
          currentTaskBefore: currentTask
        });
        
        // Force update by changing timestamp
        setLastSyncTime(Date.now());
        
        // If currentTask didn't update for some reason, manually update form
        if (task && task._id === taskId) {
          console.log('[MinimalTaskDetails] Manually updating form data');
          setFormData({
            title: task.title || '',
            description: task.description || '',
            status: task.status || 'to_do',
            priority: task.priority || 'medium',
            assignedTo: task.assignedTo?._id || task.assignedTo || null,
            dueDate: task.dueDate ? new Date(task.dueDate) : null,
            projectId: task.projectId?._id || task.projectId || '',
            sectionId: task.sectionId?._id || task.sectionId || '',
            type: task.type || 'general',
            estimatedHours: task.estimatedHours || '',
            actualHours: task.actualHours || '',
            isClientVisible: task.isClientVisible || false,
            milestoneId: task.milestoneId || '',
            contentStage: task.contentStage || 'todo',
            contentType: task.content?.type || 'post',
            category: task.content?.category || '',
            language: task.content?.language || 'bilingual',
            hashtags: task.content?.hashtags || [],
            platforms: task.content?.platforms || (task.content?.distributions ? task.content.distributions.map(d => d.platform) : []),
          });
        }
      } catch (error) {
        console.error('[MinimalTaskDetails] Error refreshing task:', error);
      } finally {
        // Hide syncing indicator after a short delay
        setTimeout(() => setIsSyncing(false), 500);
      }
    }
  }, [taskId, isCreateMode, fetchTask, currentTask]);
  
  // Debug socket connection
  useEffect(() => {
    console.log('[MinimalTaskDetails] Setting up socket listeners for taskId:', taskId);
  }, [taskId]);
  
  // Direct socket event handling for this specific task
  useSocketEvent('task:updated', (data) => {
    console.log('[MinimalTaskDetails] task:updated event received:', {
      data,
      taskId,
      taskIdFromEvent: data?.task?._id,
      matches: data?.task?._id === taskId
    });
    if (data?.task && data.task._id === taskId) {
      console.log('[MinimalTaskDetails] This task was updated, refreshing...');
      refreshTask();
    }
  });
  
  useSocketEvent('task:status_changed', (data) => {
    if (data.task && data.task._id === taskId) {
      console.log('[MinimalTaskDetails] Task status changed, refreshing...');
      refreshTask();
    }
  });
  
  useSocketEvent('task:priority_changed', (data) => {
    if (data.task && data.task._id === taskId) {
      console.log('[MinimalTaskDetails] Task priority changed, refreshing...');
      refreshTask();
    }
  });
  
  useSocketEvent('task:due_date_changed', (data) => {
    if (data.task && data.task._id === taskId) {
      console.log('[MinimalTaskDetails] Task due date changed, refreshing...');
      refreshTask();
    }
  });
  
  useSocketEvent('task:progress_updated', (data) => {
    if (data.task && data.task._id === taskId) {
      console.log('[MinimalTaskDetails] Task progress updated, refreshing...');
      refreshTask();
    }
  });
  
  useSocketEvent('task:attachment_uploaded', (data) => {
    if (data.task && data.task._id === taskId) {
      console.log('[MinimalTaskDetails] Attachment uploaded, refreshing...');
      refreshTask();
    }
  });
  
  useSocketEvent('task:attachment_deleted', (data) => {
    if (data.task && data.task._id === taskId) {
      console.log('[MinimalTaskDetails] Attachment deleted, refreshing...');
      refreshTask();
    }
  });

  // Timer-related socket events
  useSocketEvent('task:timer_started', (data) => {
    if (data.task && data.task._id === taskId) {
      console.log('[MinimalTaskDetails] Timer started, refreshing...');
      refreshTask();
    }
  });

  useSocketEvent('task:timer_stopped', (data) => {
    if (data.task && data.task._id === taskId) {
      console.log('[MinimalTaskDetails] Timer stopped, refreshing...');
      refreshTask();
    }
  });
  
  // Also join the project room if we have a projectId
  const { socket } = useSocket();
  const connected = useSocketConnection();
  
  // Update current time every second for time tracking
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    if (socket && formData.projectId && connected) {
      console.log('[MinimalTaskDetails] Joining project room:', formData.projectId);
      socket.emit('join:project', formData.projectId);
      
      return () => {
        socket.emit('leave:project', formData.projectId);
      };
    }
  }, [socket, formData.projectId, connected]);
  
  // Load task data when taskId changes or component mounts
  useEffect(() => {
    if (propTaskId && !isCreateMode) {
      // Always fetch the task when propTaskId is provided
      // This ensures we have fresh data including timeEntries
      setIsInitialLoading(true);
      console.log('[MinimalTaskDetails] Fetching task with ID:', propTaskId);
      fetchTask(propTaskId).finally(() => {
        setIsInitialLoading(false);
      });
    }
  }, [propTaskId, isCreateMode, fetchTask]);
  
  // Debug current task updates
  useEffect(() => {
    console.log('[MinimalTaskDetails] currentTask updated:', {
      taskId: currentTask?._id,
      title: currentTask?.title,
      timeEntriesCount: currentTask?.timeEntries?.length || 0,
      hasActiveTimer: currentTask?.timeEntries?.some(e => e.isActive) || false,
      timeEntries: currentTask?.timeEntries
    });
  }, [currentTask]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (editingTimeoutRef.current) {
        clearTimeout(editingTimeoutRef.current);
      }
    };
  }, []);
  
  // Mark as editing when user types
  const markAsEditing = useCallback(() => {
    setIsEditing(true);
    // Clear existing timeout
    if (editingTimeoutRef.current) {
      clearTimeout(editingTimeoutRef.current);
    }
    // Reset editing flag after 2 seconds of inactivity
    editingTimeoutRef.current = setTimeout(() => {
      setIsEditing(false);
    }, 2000);
  }, []);
  
  // Update form when task loads or gets updated via real-time events
  useEffect(() => {
    if (currentTask && !isCreateMode && currentTask._id === taskId) {
      // Always update when lastSyncTime changes (real-time update)
      const isRealtimeUpdate = lastSyncTime > 0;
      
      // Don't update if user is actively editing (unless it's a real-time update)
      if (isEditing && !isRealtimeUpdate) {
        console.log('[MinimalTaskDetails] Skipping form update - user is editing');
        return;
      }
      
      console.log('[MinimalTaskDetails] Updating form data from currentTask:', {
        taskId: currentTask._id,
        title: currentTask.title,
        status: currentTask.status,
        updatedAt: currentTask.updatedAt,
        isRealtimeUpdate,
        lastSyncTime,
        hasContent: !!currentTask.content,
        contentType: typeof currentTask.content,
        contentData: currentTask.content,
        contentStage: currentTask.contentStage,
        isCreateMode: isCreateMode
      });
      
      setFormData(prevData => ({
        title: currentTask.title || '',
        description: currentTask.description || '',
        status: currentTask.status || 'to_do',
        priority: currentTask.priority || 'medium',
        assignedTo: currentTask.assignedTo?._id || currentTask.assignedTo || null,
        dueDate: currentTask.dueDate ? new Date(currentTask.dueDate) : null,
        projectId: currentTask.projectId?._id || currentTask.projectId || '',
        sectionId: currentTask.sectionId?._id || currentTask.sectionId || '',
        type: currentTask.type || 'general',
        estimatedHours: currentTask.estimatedHours || '',
        actualHours: currentTask.actualHours || '',
        isClientVisible: currentTask.isClientVisible || false,
        milestoneId: currentTask.milestoneId || '',
        contentStage: currentTask.contentStage || 'todo',
        contentType: currentTask.content?.type || 'post',
        category: currentTask.content?.category || '',
        language: currentTask.content?.language || 'bilingual',
        hashtags: currentTask.content?.hashtags || [],
        platforms: currentTask.content?.platforms || (currentTask.content?.distributions ? currentTask.content.distributions.map(d => d.platform) : []),
      }));
    }
  }, [currentTask, isCreateMode, taskId, lastSyncTime]); // Simplified dependencies
  
  // Set default values for content creation projects in create mode
  useEffect(() => {
    if (isCreateMode && currentProject?.type === 'content_creation') {
      const defaultCategories = getDefaultCategoriesForType(formData.contentType || 'post');
      setFormData(prev => ({
        ...prev,
        category: prev.category || defaultCategories[0] || 'Product',
        contentType: prev.contentType || 'post',
        language: prev.language || 'bilingual',
        contentStage: prev.contentStage || 'todo'
      }));
    }
  }, [isCreateMode, currentProject?.type, formData.contentType]);
  
  
  // Initialize debounced background save - combines debouncing with persistent queue
  const {
    fieldSaveStatus,
    hasUnsavedChanges,
    queueStatus,
    triggerSave: triggerAutoSave,
    flushPendingSaves,
    getFieldStatus,
    getQueueInfo
  } = useDebouncedBackgroundSave({
    onSave: async (field, value, capturedTaskId) => {
      // Use the captured taskId that was passed when the save was queued
      const validTaskId = capturedTaskId || taskId || currentTask?._id;
      
      if (isCreateMode || !validTaskId) {
        console.warn(`[MinimalTaskDetails] Skipping save - ${isCreateMode ? 'in create mode' : 'no valid task ID'}`);
        return; // Silently skip instead of throwing error
      }
      
      // All fields including content fields go through task API
      const result = await patchTask(validTaskId, { [field]: value });
      return result;
    },
    taskId,
    debounceMs: 800, // Wait 800ms after user stops typing
    onError: (field, value, previousValue, error) => {
      console.error(`Failed to save ${field}:`, error);
      showError(error.message || `Failed to save ${field}`);
      
      // Rollback the field value on error
      if (previousValue !== undefined) {
        setFormData(prev => ({ ...prev, [field]: previousValue }));
      }
    },
    onSuccess: (field, value) => {
      // Silent success - no toast notification
    }
  });
  
  // Handle field updates
  const updateField = useCallback((field, value) => {
    // Mark as editing to prevent real-time updates from overwriting
    markAsEditing();
    
    // Get the previous value for potential rollback
    const previousValue = formData[field];
    
    // Optimistic update - update UI immediately
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Only trigger auto-save if not in create mode and we have a task ID
    if (!isCreateMode && taskId) {
      triggerAutoSave(field, value, previousValue);
    }
  }, [triggerAutoSave, markAsEditing, formData, isCreateMode, taskId]);
  
  // Toggle section expansion
  const toggleSection = useCallback((section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);
  
  // Handle form submission (only for create mode now)
  const handleCreateTask = async () => {
    if (!formData.title.trim()) {
      showError('Task title is required');
      return null;
    }
    
    setIsSubmitting(true);
    try {
      // Prepare data for submission, merging form data with create context
      const taskData = { ...formData, ...taskCreateContext };
        
        
        // If it's a content creation project, prepare content data
        if (isContentCreationProject) {
          // Get valid categories for the content type
          const contentType = taskData.contentType || 'post';
          const validCategories = getDefaultCategoriesForType(contentType);
          
          // Prepare content data with required field defaults
          const contentData = {
            title: taskData.title || 'Untitled Content',
            text: taskData.description || '',
            type: contentType,
            stage: taskData.contentStage || 'todo',
            category: taskData.category || validCategories[0] || 'Product', // Use first valid category
            language: taskData.language || 'bilingual',
            hashtags: taskData.hashtags || [],
            // Convert platforms to distributions
            distributions: (taskData.platforms || []).map(platform => ({
              platform,
              status: taskData.dueDate ? 'scheduled' : 'draft',
              scheduledTime: taskData.dueDate || null
            }))
          };
          
          // Remove content fields from task data
          delete taskData.contentType;
          delete taskData.category;
          delete taskData.language;
          delete taskData.hashtags;
          delete taskData.platforms;
          
          // Get the createTaskWithContentAndFiles function from store
          const createTaskWithContentAndFiles = useTaskStore.getState().createTaskWithContentAndFiles;
          const result = await createTaskWithContentAndFiles(taskData, contentData, [], []);
          showSuccess('Task created successfully');
          if (onSubmit) onSubmit(result);
        } else {
          const result = await createTask(taskData);
          showSuccess('Task created successfully');
          if (onSubmit) onSubmit(result);
          return result;
        }
    } catch (error) {
      showError('Failed to create task');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Simple close handler
  const handleClose = useCallback(() => {
    // Flush any pending debounced saves to the queue before closing
    if (!isCreateMode && hasUnsavedChanges) {
      flushPendingSaves();
    }
    
    // For create mode, create task if title exists
    if (isCreateMode && formData.title.trim()) {
      handleCreateTask().then(result => {
        if (result && onClose) {
          onClose();
        }
      });
      return;
    }
    
    // Otherwise just close
    if (onClose) onClose();
  }, [hasUnsavedChanges, isCreateMode, formData.title, handleCreateTask, onClose, flushPendingSaves, getQueueInfo]);
  
  
  // Handle delete task
  const handleDeleteTask = async () => {
    if (!taskId || isCreateMode) return;
    
    setIsSubmitting(true);
    try {
      await deleteTask(taskId);
      showSuccess('Task deleted successfully');
      if (onClose) onClose();
    } catch (error) {
      showError('Failed to delete task');
    } finally {
      setIsSubmitting(false);
      setDeleteMenuAnchor(null);
    }
  };
  
  // Handle share task
  const handleShareTask = async () => {
    if (!taskId || isCreateMode) return;
    
    try {
      const success = await copyTaskLink(taskId);
      if (success) {
        showSuccess('Task link copied to clipboard');
      } else {
        showError('Failed to copy link');
      }
    } catch (error) {
      showError('Failed to copy link');
    } finally {
      setShareMenuAnchor(null);
    }
  };
  
  // Handle status toggle
  const handleStatusToggle = async () => {
    if (!taskId || isCreateMode) return;
    
    try {
      // Ensure we have the current task
      if (!currentTask || currentTask._id !== taskId) {
        await fetchTask(taskId);
      }
      
      const newStatus = formData.status === 'done' ? 'to_do' : 'done';
      
      // Build update data
      const updateData = { status: newStatus };
      
      // Auto-calculate actual hours when marking as done
      if (newStatus === 'done' && currentTask && !formData.actualHours) {
        const createdAt = new Date(currentTask.createdAt);
        const now = new Date();
        const hoursElapsed = Math.round((now - createdAt) / (1000 * 60 * 60) * 10) / 10;
        updateData.actualHours = hoursElapsed;
        updateField('actualHours', hoursElapsed);
      }
      
      // Update status locally first for immediate feedback
      updateField('status', newStatus);
      
      // Then update on server
      await patchTask(taskId, updateData);
      showSuccess(`Task marked as ${newStatus === 'done' ? 'complete' : 'incomplete'}`);
    } catch (error) {
      console.error('Status toggle error:', error);
      showError(error.message || 'Failed to update status');
      // Revert status on error
      updateField('status', formData.status);
    }
  };
  
  // Get current project - try from projects array first, then currentProject as fallback
  const currentProjectData = projects.find(p => p._id === formData.projectId) || 
    (currentProject?._id === formData.projectId ? currentProject : null);
  
  // Check if this is a content creation project
  const isContentCreationProject = currentProjectData?.type === 'content_creation';
  
  // Use the explicit props to control field visibility
  const shouldHideProjectField = hideProjectField;
  const shouldHideSectionField = hideSectionField;
  
  // Get sections for the current project
  const currentProjectSections = formData.projectId ? projectSections[formData.projectId] || [] : [];
  
  // Fetch sections when project changes
  useEffect(() => {
    if (formData.projectId && (!projectSections[formData.projectId] || projectSections[formData.projectId].length === 0)) {
      fetchProjectSections(formData.projectId);
    }
  }, [formData.projectId, fetchProjectSections]); // Removed projectSections to prevent loops
  
  // Fetch projects if not loaded
  useEffect(() => {
    if (!projects.length) {
      fetchProjects();
    }
  }, [projects.length, fetchProjects]);

  // Track if we started in create mode and haven't created a task yet
  const startedInCreateModeRef = useRef(isCreateMode);
  const isCreatingRef = useRef(false); // Prevent duplicate creation
  
  // Keep a ref to formData for use in cleanup
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  });
  
  // Flush pending saves when component unmounts (e.g., drawer closes)
  useEffect(() => {
    return () => {
      // If we started in create mode, create task if title exists
      if (startedInCreateModeRef.current && formDataRef.current.title.trim() && !isCreatingRef.current) {
        // Set flag to prevent duplicate creation
        isCreatingRef.current = true;
        
        const taskData = { ...formDataRef.current, ...taskCreateContext };
        
        // Ensure required fields
        if (!taskData.projectId && currentProject?._id) {
          taskData.projectId = currentProject._id;
        }
        
        // Create the task
        createTask(taskData).then(result => {
          if (result) {
            showSuccess('Task created successfully');
          }
        }).catch(error => {
          console.error('Failed to create task on close:', error);
        });
      } else if (!startedInCreateModeRef.current) {
        // For edit mode, flush pending saves
        flushPendingSaves();
      }
    };
  }, [flushPendingSaves, createTask, showSuccess, taskCreateContext, currentProject]);

  // Warn user about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !isCreateMode) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, isCreateMode]);

  // Fetch project data when task is loaded to ensure team members are available
  useEffect(() => {
    if (currentTask && currentTask.projectId) {
      const projectId = currentTask.projectId._id || currentTask.projectId;
      // Only fetch if the current project is different or not loaded
      if (!currentProject || currentProject._id !== projectId) {
        fetchProject(projectId);
      }
    }
  }, [currentTask, currentProject, fetchProject]);
  
  // Count items in sections for preview
  const sectionCounts = useMemo(() => ({
    details: [
      !shouldHideProjectField && formData.projectId,
      !shouldHideSectionField && formData.sectionId,
      formData.type !== 'general',
      formData.milestoneId,
      formData.estimatedHours,
      formData.actualHours,
      formData.isClientVisible,
    ].filter(Boolean).length,
    content: isContentCreationProject ? [
      formData.contentType,
      formData.category,
      formData.language,
      formData.hashtags?.length > 0,
      formData.platforms?.length > 0,
    ].filter(Boolean).length : 0,
    checklist: currentTask?.checklist?.length || 0,
    subtasks: currentTask?.subtaskCount !== undefined ? currentTask.subtaskCount : currentTask?.subtasks?.length || 0,
    attachments: currentTask?.attachments?.length || 0,
    discussion: currentTask?.comments?.length || 0,
    activity: currentTask?.activities?.length || 0,
  }), [formData, currentTask, isContentCreationProject]);
  
  // Loading state
  if (isInitialLoading && !isCreateMode) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" height={40} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={48} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={200} />
      </Box>
    );
  }
  
  // Error state
  if (error && !isCreateMode) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        <Button onClick={() => fetchTask(taskId)}>Retry</Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      bgcolor: 'background.paper',
    }}>
      {/* Removed global save status indicator to prevent flickering */}
      
      {/* Header removed - drawer provides its own header */}
      
      {/* Main Content - Scrollable */}
      <Box sx={{ 
        flexGrow: 1, 
        overflowY: 'scroll', // Always show scrollbar to prevent layout shift
        overflowX: 'hidden',
        p: isMobile ? 2 : 3,
        bgcolor: 'background.paper',
        position: 'relative',
        // Ensure consistent scrollbar gutter
        scrollbarGutter: 'stable',
      }}>
        {/* Sync indicator - now less prominent since we have save status */}
        {isSyncing && (
          <Fade in={isSyncing}>
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                px: 1,
                py: 0.25,
                borderRadius: 1,
                zIndex: 10,
              }}
            >
              <CircularProgress size={12} />
              <Typography variant="caption" color="text.secondary" fontSize={11}>
                Syncing...
              </Typography>
            </Box>
          </Fade>
        )}
        
        {/* Task Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 2, 
          mb: 2 
        }}>
          {/* Title */}
          <Box sx={{ flexGrow: 1 }}>
            <InlineEditField
              value={formData.title}
              onChange={(value) => updateField('title', value)}
              placeholder="What needs to be done?"
              variant="title"
              autoFocus={isCreateMode}
              error={!formData.title && isSubmitting}
              // saveStatus={!isCreateMode ? getFieldStatus('title') : undefined} // Disabled to prevent flickering
            />
          </Box>
          
          {/* Share Button - only show for existing tasks */}
          {!isCreateMode && (
            <IconButton
              size="small"
              onClick={handleShareTask}
              sx={{
                mt: 0.5,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
              title="Copy task link"
            >
              <ShareIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        
        {/* Status and meta bar */}
        <Box sx={{ mb: 2 }}>
          <TaskMetaBar
            status={formData.status}
            priority={formData.priority}
            assignedTo={formData.assignedTo}
            dueDate={formData.dueDate}
            onStatusChange={(value) => updateField('status', value)}
            onPriorityChange={(value) => updateField('priority', value)}
            onAssigneeChange={(value) => updateField('assignedTo', value)}
            onDueDateChange={(value) => updateField('dueDate', value)}
            isMobile={isMobile}
            saveStatus={{
              status: getFieldStatus('status'),
              priority: getFieldStatus('priority'),
              assignedTo: getFieldStatus('assignedTo'),
              dueDate: getFieldStatus('dueDate')
            }}
          />
        </Box>
        
        {/* Project and Section fields - Asana style (more compact) */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          mb: 2.5,
          alignItems: 'center'
        }}>
          {/* Project Selector */}
          <Box sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: 0.5,
            px: 0.4,
            py: 0.2,
            borderRadius: 0.5,
            height: 22,
            bgcolor: theme.palette.mode === 'dark' 
              ? alpha(theme.palette.common.white, 0.05)
              : alpha(theme.palette.common.black, 0.04),
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.08)
                : alpha(theme.palette.common.black, 0.08),
            }
          }}>
            <Box sx={{
              width: 10,
              height: 10,
              borderRadius: '2px',
              bgcolor: currentProjectData ? theme.palette.primary.main : theme.palette.grey[400],
              flexShrink: 0
            }} />
            <FormControl size="small" variant="standard" sx={{ 
              m: 0,
              minWidth: 0,
              '& .MuiInput-root': {
                m: '0 !important',
                height: 'auto !important',
                minHeight: '0 !important',
                mt: '0 !important',
                mb: '0 !important',
                '&:before, &:after': {
                  display: 'none !important'
                }
              },
              '& .MuiInputBase-root': {
                height: 'auto !important',
                minHeight: '0 !important',
                mt: '0 !important',
                mb: '0 !important'
              }
            }}>
              <Select
                value={formData.projectId || ''}
                onChange={(e) => updateField('projectId', e.target.value)}
                displayEmpty
                sx={{
                  fontSize: '0.75rem',
                  lineHeight: 1,
                  '&.MuiInputBase-root': {
                    mt: '0 !important',
                    mb: '0 !important',
                  },
                  '& .MuiSelect-select': {
                    padding: '0 20px 0 0 !important',
                    display: 'flex',
                    alignItems: 'center',
                    minHeight: 'auto !important',
                    lineHeight: 1,
                  },
                  '&:before': {
                    display: 'none'
                  },
                  '&:after': {
                    display: 'none'
                  },
                  '& .MuiSelect-icon': {
                    right: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: 14
                  }
                }}
              >
                {projects.map(p => (
                  <MenuItem key={p._id} value={p._id} sx={{ fontSize: '0.8125rem', py: 0.5 }}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          
          {/* Section Selector */}
          {formData.projectId && (
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 0.4,
              px: 0.4,
              py: 0.2,
              borderRadius: 0.5,
              height: 22,
              bgcolor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.common.white, 0.05)
                : alpha(theme.palette.common.black, 0.04),
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.08)
                  : alpha(theme.palette.common.black, 0.08),
              }
            }}>
              <SectionIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
              <FormControl size="small" variant="standard" sx={{ 
                m: 0,
                minWidth: 0,
                '& .MuiInput-root': {
                  m: '0 !important',
                  height: 'auto !important',
                  minHeight: '0 !important',
                  mt: '0 !important',
                  mb: '0 !important',
                  '&:before, &:after': {
                    display: 'none !important'
                  }
                },
                '& .MuiInputBase-root': {
                  height: 'auto !important',
                  minHeight: '0 !important',
                  mt: '0 !important',
                  mb: '0 !important'
                }
              }}>
                <Select
                  value={formData.sectionId || ''}
                  onChange={(e) => updateField('sectionId', e.target.value)}
                  displayEmpty
                  sx={{
                    fontSize: '0.75rem',
                    lineHeight: 1,
                    '&.MuiInputBase-root': {
                      mt: '0 !important',
                      mb: '0 !important',
                    },
                    '& .MuiSelect-select': {
                      padding: '0 20px 0 0 !important',
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: 'auto !important',
                      lineHeight: 1,
                    },
                    '&:before': {
                      display: 'none'
                    },
                    '&:after': {
                      display: 'none'
                    },
                    '& .MuiSelect-icon': {
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: 14
                    }
                  }}
                >
                  {currentProjectSections.map(s => (
                    <MenuItem key={s._id} value={s._id} sx={{ fontSize: '0.8125rem', py: 0.5 }}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
        
        {/* Description with mentions - Enhanced UI */}
        <TaskDescriptionField
          value={formData.description}
          onChange={(value) => updateField('description', value)}
          onMentionedUsersChange={(users) => {
            // Handle mentioned users if needed
          }}
          placeholder="Add description... Use @ to mention team members"
          multiline
          rows={3}
          disabled={isSubmitting}
          sx={{ mb: 2 }}
        />
           {/* Compact Content Details - Status Bar Style */}
        {isContentCreationProject && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Content Details
            </Typography>
            
            <Box sx={{ 
              p: 1,
              bgcolor: alpha(theme.palette.background.paper, 0.5),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              display: 'flex', 
              alignItems: 'center',
              gap: 0.5,
              overflowX: 'auto',
              overflowY: 'hidden',
              // Hide scrollbar but keep functionality
              '&::-webkit-scrollbar': {
                height: 0,
                display: 'none'
              },
              scrollbarWidth: 'none',
            }}>
              {/* Chips container - no wrap */}
              <Box sx={{ 
                display: 'flex', 
                gap: 0.5,
                alignItems: 'center',
                flexShrink: 0,
              }}>
                {/* Content Type */}
                <Chip
                  size="small"
                  icon={<TypeIcon sx={{ fontSize: 13 }} />}
                  label={CONTENT_TYPES.find(t => t.value === formData.contentType)?.label || 'Post'}
                  onClick={(e) => setContentTypeAnchor(e.currentTarget)}
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.15),
                    },
                    '& .MuiChip-icon': {
                      fontSize: 13,
                      ml: 0.5
                    },
                    '& .MuiChip-label': {
                      px: 0.75
                    }
                  }}
                />
                
                {/* Category */}
                <Chip
                  size="small"
                  icon={<SectionIcon sx={{ fontSize: 13 }} />}
                  label={formData.category || 'Product'}
                  onClick={(e) => setCategoryAnchor(e.currentTarget)}
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.secondary.main, 0.15),
                    },
                    '& .MuiChip-icon': {
                      fontSize: 13,
                      ml: 0.5
                    },
                    '& .MuiChip-label': {
                      px: 0.75
                    }
                  }}
                />
                
                {/* Language */}
                <Chip
                  size="small"
                  icon={<LanguageIcon sx={{ fontSize: 13 }} />}
                  label={LANGUAGE_OPTIONS.find(l => l.value === formData.language)?.label || 'Bilingual'}
                  onClick={(e) => setLanguageAnchor(e.currentTarget)}
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.info.main, 0.15),
                    },
                    '& .MuiChip-icon': {
                      fontSize: 13,
                      ml: 0.5
                    },
                    '& .MuiChip-label': {
                      px: 0.75
                    }
                  }}
                />
                
                {/* Hashtags */}
                <Chip
                  size="small"
                  icon={<TagIcon sx={{ fontSize: 13 }} />}
                  label={formData.hashtags?.length ? `${formData.hashtags.length} tags` : 'Add tags'}
                  onClick={(e) => setHashtagsAnchor(e.currentTarget)}
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: formData.hashtags?.length 
                      ? alpha(theme.palette.success.main, 0.08)
                      : alpha(theme.palette.action.hover, 0.08),
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: formData.hashtags?.length
                        ? alpha(theme.palette.success.main, 0.15)
                        : alpha(theme.palette.action.hover, 0.15),
                    },
                    '& .MuiChip-icon': {
                      fontSize: 13,
                      ml: 0.5
                    },
                    '& .MuiChip-label': {
                      px: 0.75
                    }
                  }}
                />
                
                {/* Platforms */}
                <Chip
                  size="small"
                  icon={<ShareIcon sx={{ fontSize: 13 }} />}
                  label={formData.platforms?.length ? `${formData.platforms.length} platforms` : 'Add platforms'}
                  onClick={(e) => setPlatformsAnchor(e.currentTarget)}
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: formData.platforms?.length 
                      ? alpha(theme.palette.warning.main, 0.08)
                      : alpha(theme.palette.action.hover, 0.08),
                    flexShrink: 0,
                    '&:hover': {
                      bgcolor: formData.platforms?.length
                        ? alpha(theme.palette.warning.main, 0.15)
                        : alpha(theme.palette.action.hover, 0.15),
                    },
                    '& .MuiChip-icon': {
                      fontSize: 13,
                      ml: 0.5
                    },
                    '& .MuiChip-label': {
                      px: 0.75
                    }
                  }}
                />
              </Box>
              
              {/* Stage Badge - aligned to the right */}
              <Box sx={{ 
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexShrink: 0,
                pr: 0.5
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: {
                    todo: theme.palette.grey[500],
                    idea: theme.palette.info.main,
                    reference: theme.palette.secondary.main,
                    creative: theme.palette.primary.main,
                    posting: theme.palette.warning.main,
                    completed: theme.palette.success.main
                  }[formData.contentStage] || theme.palette.grey[500]
                }} />
                <Typography 
                  variant="caption" 
                  sx={{ 
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      color: 'text.primary'
                    }
                  }}
                  onClick={(e) => setStageAnchor(e.currentTarget)}
                >
                  {CONTENT_STAGES.find(s => s.value === formData.contentStage)?.label || 'To Do'}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
          {/* Compact Time Tracking Section - only for existing tasks */}
        {!isCreateMode && currentTask && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              Time Tracking
            </Typography>
            
            {/* Compact Time Tracking Display */}
            <Box sx={{
              p: 1.5,
              bgcolor: alpha(theme.palette.background.paper, 0.5),
              borderRadius: 1,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}>
              {/* Time Display */}
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                
                {(() => {
                  const timeEntries = currentTask?.timeEntries || [];
                  const myActiveTimer = timeEntries.find(entry => {
                    const entryUserId = entry.user?._id || entry.user;
                    return entry.isActive && entryUserId === user?._id;
                  });
                  
                  // Calculate total time
                  const totalTime = timeEntries.reduce((sum, entry) => {
                    if (entry.isActive) {
                      return sum + (currentTime - new Date(entry.startTime).getTime());
                    }
                    return sum + (entry.duration || 0);
                  }, 0);
                  
                  const formatDuration = (ms) => {
                    if (!ms || ms <= 0) return '0m';
                    const totalSeconds = Math.floor(ms / 1000);
                    const hours = Math.floor(totalSeconds / 3600);
                    const minutes = Math.floor((totalSeconds % 3600) / 60);
                    const seconds = totalSeconds % 60;
                    
                    if (hours > 0) {
                      return `${hours}h ${minutes}m`;
                    } else if (minutes > 0) {
                      return `${minutes}m ${seconds}s`;
                    } else {
                      return `${seconds}s`;
                    }
                  };
                  
                  const handleStartTimer = async () => {
                    try {
                      await timeTrackingService.startTimer(currentTask._id);
                      showSuccess('Timer started');
                      // Reload task
                      const response = await taskService.getTask(taskId);
                      const updatedTask = response.data?.task || response.task;
                      if (updatedTask) {
                        useTaskStore.setState((state) => ({ 
                          currentTask: {
                            ...updatedTask,
                            content: state.currentTask?.content || updatedTask.content
                          }
                        }));
                      }
                    } catch (error) {
                      showError(error.response?.data?.message || 'Failed to start timer');
                    }
                  };
                  
                  const handleStopTimer = async () => {
                    try {
                      await timeTrackingService.stopTimer(currentTask._id);
                      showSuccess('Timer stopped');
                      // Reload task
                      const response = await taskService.getTask(taskId);
                      const updatedTask = response.data?.task || response.task;
                      if (updatedTask) {
                        useTaskStore.setState((state) => ({ 
                          currentTask: {
                            ...updatedTask,
                            content: state.currentTask?.content || updatedTask.content
                          }
                        }));
                      }
                    } catch (error) {
                      showError(error.response?.data?.message || 'Failed to stop timer');
                    }
                  };
                  
                  return (
                    <>
                      {/* Total Time */}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatDuration(totalTime)}
                      </Typography>
                      
                      {timeEntries.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          ({timeEntries.length} {timeEntries.length === 1 ? 'session' : 'sessions'})
                        </Typography>
                      )}
                      
                      {/* Active Timer Indicator */}
                      {myActiveTimer && (
                        <Chip
                          label={`Running: ${formatDuration(currentTime - new Date(myActiveTimer.startTime).getTime())}`}
                          size="small"
                          color="success"
                          sx={{ height: 20, fontSize: '0.75rem' }}
                        />
                      )}
                      
                      {/* Timer Controls */}
                      <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                        {myActiveTimer ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            startIcon={<StopIcon sx={{ fontSize: 16 }} />}
                            onClick={handleStopTimer}
                            sx={{ 
                              textTransform: 'none',
                              fontSize: '0.8125rem',
                              py: 0.5,
                              px: 1.5
                            }}
                          >
                            Stop
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<PlayIcon sx={{ fontSize: 16 }} />}
                            onClick={handleStartTimer}
                            sx={{ 
                              textTransform: 'none',
                              fontSize: '0.8125rem',
                              py: 0.5,
                              px: 1.5
                            }}
                          >
                            Start
                          </Button>
                        )}
                        
                        {/* View Log Button */}
                        {timeEntries.length > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<HistoryIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setTimeTrackerDialog(true)}
                            sx={{ 
                              textTransform: 'none',
                              fontSize: '0.8125rem',
                              py: 0.5,
                              px: 1.5
                            }}
                          >
                            View Log
                          </Button>
                        )}
                      </Box>
                    </>
                  );
                })()}
              </Box>
            </Box>
          </Box>
        )}
        {/* Checklist Section */}
        {!isCreateMode && (
          <CollapsibleSection
            title="Checklist"
            count={currentTask?.checklist?.filter(item => !item.isCompleted).length || 0}
            expanded={expandedSections.checklist}
            onToggle={() => toggleSection('checklist')}
            preview={
              currentTask?.checklist?.length > 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {currentTask.checklist.filter(item => item.isCompleted).length}/{currentTask.checklist.length} completed
                  </Typography>
                  <Box sx={{ width: 60, height: 4, bgcolor: alpha(theme.palette.action.disabled, 0.2), borderRadius: 2, overflow: 'hidden' }}>
                    <Box 
                      sx={{ 
                        height: '100%', 
                        width: `${(currentTask.checklist.filter(item => item.isCompleted).length / currentTask.checklist.length) * 100}%`,
                        bgcolor: currentTask.checklist.filter(item => item.isCompleted).length === currentTask.checklist.length 
                          ? theme.palette.success.main 
                          : theme.palette.primary.main,
                        transition: 'width 0.3s ease',
                        borderRadius: 2,
                      }} 
                    />
                  </Box>
                </Box>
              ) : (
                <Typography variant="caption" color="text.disabled">
                  No items
                </Typography>
              )
            }
            sx={{ mb: 2 }}
          >
            <TaskItems
              taskId={taskId}
              type="checklist"
            />
          </CollapsibleSection>
        )}
        
        {/* Collapsible Sections */}
        
   
        
      
        
        
     
        
        {/* Subtasks Section */}
        {!isCreateMode && (
          <CollapsibleSection
            title="Subtasks"
            count={sectionCounts.subtasks}
            expanded={expandedSections.subtasks}
            onToggle={() => toggleSection('subtasks')}
            sx={{ mb: 2 }}
          >
            <TaskSubtaskList
              parentTaskId={taskId}
              task={currentTask}
              subtasks={currentTask?.subtasks || []}
              canEdit={true}
            />
          </CollapsibleSection>
        )}
        
        {/* Horizontal Tabs for Attachments, Discussion, and Activity */}
        {!isCreateMode && (
          <Box sx={{ mt: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minHeight: 40,
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: 'text.primary',
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 2,
                  bgcolor: theme.palette.primary.main,
                },
              }}
            >
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachFileIcon sx={{ fontSize: 16 }} />
                    <span>Attachments</span>
                    {sectionCounts.attachments > 0 && (
                      <Chip 
                        label={sectionCounts.attachments} 
                        size="small" 
                        sx={{ 
                          height: 18,
                          minWidth: 18,
                          fontSize: '0.75rem',
                          ml: 0.5
                        }} 
                      />
                    )}
                  </Box>
                }
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ChatIcon sx={{ fontSize: 16 }} />
                    <span>Discussion</span>
                    {sectionCounts.discussion > 0 && (
                      <Chip 
                        label={sectionCounts.discussion} 
                        size="small" 
                        sx={{ 
                          height: 18,
                          minWidth: 18,
                          fontSize: '0.75rem',
                          ml: 0.5
                        }} 
                      />
                    )}
                  </Box>
                }
              />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <HistoryIcon sx={{ fontSize: 16 }} />
                    <span>Activity</span>
                    {sectionCounts.activity > 0 && (
                      <Chip 
                        label={sectionCounts.activity} 
                        size="small" 
                        sx={{ 
                          height: 18,
                          minWidth: 18,
                          fontSize: '0.75rem',
                          ml: 0.5
                        }} 
                      />
                    )}
                  </Box>
                }
              />
            </Tabs>
            
            {/* Tab Panels - All panels rendered with visibility control */}
            <Box sx={{ 
              pt: 2,
              position: 'relative',
              minHeight: '400px', // Reasonable minimum to prevent collapse
              '& > *': {
                transition: 'opacity 0.2s ease-in-out',
              }
            }}>
              {/* Attachments Tab */}
              <Box sx={{ 
                opacity: activeTab === 0 ? 1 : 0,
                pointerEvents: activeTab === 0 ? 'auto' : 'none',
                position: activeTab === 0 ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                right: 0,
                visibility: activeTab === 0 ? 'visible' : 'hidden',
              }}>
                <AttachmentSectionEnhanced
                  taskId={taskId}
                  attachments={currentTask?.attachments || []}
                  canEdit={true}
                />
              </Box>
              
              {/* Discussion Tab */}
              <Box sx={{ 
                opacity: activeTab === 1 ? 1 : 0,
                pointerEvents: activeTab === 1 ? 'auto' : 'none',
                position: activeTab === 1 ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                right: 0,
                visibility: activeTab === 1 ? 'visible' : 'hidden',
              }}>
                <MinimalCommentsEnhanced taskId={taskId} />
              </Box>
              
              {/* Activity Tab */}
              <Box sx={{ 
                opacity: activeTab === 2 ? 1 : 0,
                pointerEvents: activeTab === 2 ? 'auto' : 'none',
                position: activeTab === 2 ? 'relative' : 'absolute',
                top: 0,
                left: 0,
                right: 0,
                visibility: activeTab === 2 ? 'visible' : 'hidden',
              }}>
                <TaskActivityCompact taskId={taskId} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      
      {/* Footer with Collaborators and Actions */}
      {!isCreateMode && (
        <Box sx={{ 
          p: 1.5, 
          borderTop: 1, 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}>
          {/* Compact Collaborators */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            flex: 1
          }}>
            <Typography variant="caption" color="text.secondary">
              Team:
            </Typography>
            
            {/* Collaborator Avatars with Status Colors */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {(formData?.collaborators || currentTask?.collaborators || []).map((collaborator, index) => {
                const userObj = collaborator.user;
                const firstName = userObj?.firstName || '';
                const lastName = userObj?.lastName || '';
                const initials = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '??';
                const fullName = `${firstName} ${lastName}`.trim() || 'Unknown User';
                
                // Status-based border colors (matching task card design)
                const statusColors = {
                  to_do: theme.palette.grey[400],
                  in_progress: theme.palette.info.main,
                  in_review: theme.palette.warning.main,
                  done: theme.palette.success.main,
                  cancelled: theme.palette.error.main
                };
                const borderColor = statusColors[collaborator.status] || theme.palette.grey[400];
                
                const statusLabels = {
                  to_do: 'To Do',
                  in_progress: 'In Progress',
                  in_review: 'In Review',
                  done: 'Done',
                  cancelled: 'Cancelled'
                };
                const statusLabel = statusLabels[collaborator.status] || 'To Do';
                
                const deadline = collaborator.deadline ? new Date(collaborator.deadline) : null;
                const isOverdue = deadline && deadline < new Date() && collaborator.status !== 'done';
                
                return (
                  <Tooltip 
                    key={collaborator._id || index}
                    title={
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                          {fullName}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: borderColor }}>
                          {statusLabel}
                        </Typography>
                        {deadline && (
                          <Typography 
                            variant="caption" 
                            display="block" 
                            sx={{ color: isOverdue ? 'error.main' : 'inherit' }}
                          >
                            {isOverdue ? '⚠ Overdue: ' : 'Due: '}
                            {deadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </Typography>
                        )}
                      </Box>
                    }
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: theme.palette.background.paper,
                          color: theme.palette.text.primary,
                          border: `1px solid ${theme.palette.divider}`,
                          '& .MuiTooltip-arrow': {
                            color: theme.palette.background.paper,
                            '&::before': {
                              border: `1px solid ${theme.palette.divider}`,
                            }
                          },
                        },
                      },
                    }}
                  >
                    <Avatar
                      src={userObj?.photo}
                      alt={fullName}
                      sx={{ 
                        width: 28,
                        height: 28,
                        fontSize: '0.7rem',
                        bgcolor: alpha(borderColor, 0.1),
                        color: borderColor,
                        border: `2px solid ${borderColor}`,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'scale(1.1)',
                          boxShadow: `0 0 0 2px ${alpha(borderColor, 0.2)}`,
                        }
                      }}
                      onClick={(e) => {
                        // Open context menu for this collaborator
                        setCollaboratorActionMenu({ 
                          anchor: e.currentTarget, 
                          collaborator: { ...collaborator, index } 
                        });
                      }}
                    >
                      {initials}
                    </Avatar>
                  </Tooltip>
                );
              })}
              
              {/* Add Collaborator Button */}
              <Tooltip title="Add team members" arrow>
                <IconButton
                  size="small"
                  onClick={(e) => setCollaboratorMenuAnchor(e.currentTarget)}
                  sx={{ 
                    width: 28,
                    height: 28,
                    border: `2px dashed ${theme.palette.divider}`,
                    bgcolor: 'transparent',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.action.hover, 0.04),
                      borderColor: theme.palette.primary.main,
                    }
                  }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={(e) => setShareMenuAnchor(e.currentTarget)}
              title="Share task"
            >
              <ShareIcon fontSize="small" />
            </IconButton>
            
            <IconButton
              size="small"
              onClick={(e) => setDeleteMenuAnchor(e.currentTarget)}
              color="error"
              title="Delete task"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
      
      {/* Create mode - only show hint text */}
      {isCreateMode && (
        <Box sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'center',
        }}>
          <Typography variant="caption" color="text.secondary">
            {formData.title.trim() 
              ? 'Task will be created automatically when you close' 
              : 'Enter a title to create the task'}
          </Typography>
        </Box>
      )}
      
      {/* Share Menu */}
      <Menu
        anchorEl={shareMenuAnchor}
        open={Boolean(shareMenuAnchor)}
        onClose={() => setShareMenuAnchor(null)}
      >
        <MenuItem onClick={handleShareTask}>
          <ShareIcon fontSize="small" sx={{ mr: 1 }} />
          Copy Link
        </MenuItem>
      </Menu>
      
      {/* Delete Confirmation Menu */}
      <Menu
        anchorEl={deleteMenuAnchor}
        open={Boolean(deleteMenuAnchor)}
        onClose={() => setDeleteMenuAnchor(null)}
      >
        <MenuItem onClick={handleDeleteTask} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Task
        </MenuItem>
        <MenuItem onClick={() => setDeleteMenuAnchor(null)}>
          Cancel
        </MenuItem>
      </Menu>
      
      {/* Quick Add Collaborators Menu */}
      <Menu
        anchorEl={collaboratorMenuAnchor}
        open={Boolean(collaboratorMenuAnchor)}
        onClose={() => setCollaboratorMenuAnchor(null)}
        PaperProps={{
          sx: { width: 200 }
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ px: 1, pb: 0.5, display: 'block' }}>
            Add Team Members
          </Typography>
          {currentProject?.team?.filter(member => {
            const collaboratorIds = (formData?.collaborators || currentTask?.collaborators || [])
              .map(c => (c.user?._id || c.user)?.toString());
            return member.user && !collaboratorIds.includes(member.user._id?.toString());
          }).map(member => (
            <MenuItem
              key={member.user._id}
              onClick={() => {
                const currentCollaborators = formData?.collaborators || currentTask?.collaborators || [];
                const updatedCollaborators = [...currentCollaborators, {
                  user: member.user,
                  status: formData?.status || 'to_do',
                  assignedAt: new Date()
                }];
                updateField('collaborators', updatedCollaborators);
                setCollaboratorMenuAnchor(null);
              }}
              sx={{ 
                fontSize: '0.8125rem',
                py: 0.5,
                px: 1,
                borderRadius: 0.5,
                mb: 0.25,
                minHeight: 'auto',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08)
                }
              }}
            >
              <Avatar 
                src={member.user.photo}
                sx={{ 
                  width: 20, 
                  height: 20, 
                  mr: 1,
                  fontSize: '0.65rem'
                }}
              >
                {member.user.firstName?.[0]}{member.user.lastName?.[0]}
              </Avatar>
              <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                {member.user.firstName} {member.user.lastName}
              </Typography>
            </MenuItem>
          ))}
          {currentProject?.team?.filter(member => {
            const collaboratorIds = (formData?.collaborators || currentTask?.collaborators || [])
              .map(c => (c.user?._id || c.user)?.toString());
            return member.user && !collaboratorIds.includes(member.user._id?.toString());
          }).length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ py: 1, px: 1, display: 'block', textAlign: 'center' }}>
              All team members added
            </Typography>
          )}
        </Box>
      </Menu>
      
      {/* Collaborator Action Menu */}
      <Menu
        anchorEl={collaboratorActionMenu.anchor}
        open={Boolean(collaboratorActionMenu.anchor)}
        onClose={() => setCollaboratorActionMenu({ anchor: null, collaborator: null })}
        PaperProps={{
          sx: { minWidth: 200 }
        }}
      >
        {collaboratorActionMenu.collaborator && (
          <>
            {/* Status Options - Only show for current user */}
            {(collaboratorActionMenu.collaborator.user?._id === user?._id || 
              collaboratorActionMenu.collaborator.user === user?._id) && (
              <>
                <MenuItem disabled sx={{ fontSize: '0.75rem', opacity: 0.7 }}>
                  Update My Status
                </MenuItem>
            {['to_do', 'in_progress', 'in_review', 'done'].map(status => {
              const statusLabels = {
                to_do: 'To Do',
                in_progress: 'In Progress',
                in_review: 'In Review',
                done: 'Done'
              };
              const statusColors = {
                to_do: theme.palette.grey[600],
                in_progress: theme.palette.info.main,
                in_review: theme.palette.warning.main,
                done: theme.palette.success.main
              };
              return (
                <MenuItem
                  key={status}
                  onClick={() => {
                    const currentCollaborators = formData?.collaborators || currentTask?.collaborators || [];
                    const updatedCollaborators = currentCollaborators.map((c, idx) => {
                      if (idx === collaboratorActionMenu.collaborator.index) {
                        return { ...c, status };
                      }
                      return c;
                    });
                    updateField('collaborators', updatedCollaborators);
                    setCollaboratorActionMenu({ anchor: null, collaborator: null });
                  }}
                  sx={{ 
                    pl: 3,
                    fontSize: '0.875rem',
                    ...(collaboratorActionMenu.collaborator.status === status && {
                      bgcolor: alpha(statusColors[status], 0.1),
                      color: statusColors[status],
                      fontWeight: 600
                    })
                  }}
                >
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: statusColors[status],
                    mr: 1 
                  }} />
                  {statusLabels[status]}
                </MenuItem>
              );
            })}
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
            
            {/* Quick Deadline Options - One Step */}
            <Box sx={{ px: 1, py: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block', mb: 0.5 }}>
                Set Deadline
              </Typography>
              <MenuItem
                onClick={() => {
                  const today = new Date();
                  const currentCollaborators = formData?.collaborators || currentTask?.collaborators || [];
                  const updatedCollaborators = currentCollaborators.map((c, idx) => {
                    if (idx === collaboratorActionMenu.collaborator.index) {
                      return { ...c, deadline: today };
                    }
                    return c;
                  });
                  updateField('collaborators', updatedCollaborators);
                  setCollaboratorActionMenu({ anchor: null, collaborator: null });
                }}
                sx={{ fontSize: '0.8125rem', borderRadius: 0.5, mb: 0.25 }}
              >
                <CalendarIcon sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }} />
                Today
              </MenuItem>
              <MenuItem
                onClick={() => {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  const currentCollaborators = formData?.collaborators || currentTask?.collaborators || [];
                  const updatedCollaborators = currentCollaborators.map((c, idx) => {
                    if (idx === collaboratorActionMenu.collaborator.index) {
                      return { ...c, deadline: tomorrow };
                    }
                    return c;
                  });
                  updateField('collaborators', updatedCollaborators);
                  setCollaboratorActionMenu({ anchor: null, collaborator: null });
                }}
                sx={{ fontSize: '0.8125rem', borderRadius: 0.5, mb: 0.25 }}
              >
                <CalendarIcon sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }} />
                Tomorrow
              </MenuItem>
              <MenuItem
                onClick={() => {
                  const nextWeek = new Date();
                  nextWeek.setDate(nextWeek.getDate() + 7);
                  const currentCollaborators = formData?.collaborators || currentTask?.collaborators || [];
                  const updatedCollaborators = currentCollaborators.map((c, idx) => {
                    if (idx === collaboratorActionMenu.collaborator.index) {
                      return { ...c, deadline: nextWeek };
                    }
                    return c;
                  });
                  updateField('collaborators', updatedCollaborators);
                  setCollaboratorActionMenu({ anchor: null, collaborator: null });
                }}
                sx={{ fontSize: '0.8125rem', borderRadius: 0.5, mb: 0.25 }}
              >
                <CalendarIcon sx={{ fontSize: 14, mr: 1, color: 'text.secondary' }} />
                Next Week
              </MenuItem>
              <Box sx={{ px: 1, mt: 1 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Custom date"
                    value={collaboratorActionMenu.collaborator.deadline 
                      ? new Date(collaboratorActionMenu.collaborator.deadline) 
                      : null}
                    onChange={(date) => {
                      const currentCollaborators = formData?.collaborators || currentTask?.collaborators || [];
                      const updatedCollaborators = currentCollaborators.map((c, idx) => {
                        if (idx === collaboratorActionMenu.collaborator.index) {
                          return { ...c, deadline: date };
                        }
                        return c;
                      });
                      updateField('collaborators', updatedCollaborators);
                      setCollaboratorActionMenu({ anchor: null, collaborator: null });
                    }}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: { 
                          '& .MuiInputBase-root': { 
                            fontSize: '0.8125rem',
                            height: 32,
                            borderRadius:0.5
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: '0.8125rem',
                            transform: 'translate(14px, 6px) scale(1)',
                            '&.MuiInputLabel-shrink': {
                              transform: 'translate(14px, -6px) scale(0.75)'
                            }
                          },
                          '& .MuiInputBase-input': {
                            fontSize: '0.8125rem',
                            padding: '4px 8px'
                          },
                          '& .MuiInputAdornment-root .MuiSvgIcon-root': {
                            fontSize: '1rem'  // Smaller calendar icon in the input
                          },
                          '& .MuiIconButton-root': {
                            padding: '2px'  // Reduce padding around the icon button
                          }
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
              {collaboratorActionMenu.collaborator.deadline && (
                <MenuItem
                  onClick={() => {
                    const currentCollaborators = formData?.collaborators || currentTask?.collaborators || [];
                    const updatedCollaborators = currentCollaborators.map((c, idx) => {
                      if (idx === collaboratorActionMenu.collaborator.index) {
                        return { ...c, deadline: null };
                      }
                      return c;
                    });
                    updateField('collaborators', updatedCollaborators);
                    setCollaboratorActionMenu({ anchor: null, collaborator: null });
                  }}
                  sx={{ fontSize: '0.8125rem', borderRadius: 0.5, color: 'text.secondary' }}
                >
                  <CloseIcon sx={{ fontSize: 14, mr: 1 }} />
                  Clear Deadline
                </MenuItem>
              )}
            </Box>
            
            {/* Remove old deadline code */}
            {false && collaboratorActionMenu.collaborator.deadline && (
              <MenuItem
                onClick={() => {
                  const currentCollaborators = formData?.collaborators || currentTask?.collaborators || [];
                  const updatedCollaborators = currentCollaborators.map((c, idx) => {
                    if (idx === collaboratorActionMenu.collaborator.index) {
                      return { ...c, deadline: null };
                    }
                    return c;
                  });
                  updateField('collaborators', updatedCollaborators);
                  setCollaboratorActionMenu({ anchor: null, collaborator: null });
                }}
                sx={{ fontSize: '0.875rem', pl: 3 }}
              >
                Clear Deadline
              </MenuItem>
            )}
            
            <Divider sx={{ my: 0.5 }} />
            
            {/* Remove Collaborator */}
            <MenuItem
              onClick={() => {
                setRemoveCollaboratorConfirm(collaboratorActionMenu.collaborator);
                setCollaboratorActionMenu({ anchor: null, collaborator: null });
              }}
              sx={{ 
                fontSize: '0.875rem',
                color: 'error.main',
                '&:hover': {
                  bgcolor: alpha(theme.palette.error.main, 0.08)
                }
              }}
            >
              <CloseIcon sx={{ fontSize: 16, mr: 1 }} />
              Remove from Task
            </MenuItem>
          </>
        )}
      </Menu>
      
      {/* Remove Collaborator Confirmation Dialog */}
      <Dialog
        open={Boolean(removeCollaboratorConfirm)}
        onClose={() => setRemoveCollaboratorConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.1rem' }}>Remove Team Member?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to remove{' '}
            <strong>
              {removeCollaboratorConfirm?.user?.firstName} {removeCollaboratorConfirm?.user?.lastName}
            </strong>{' '}
            from this task?
          </Typography>
          {removeCollaboratorConfirm?.deadline && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Their deadline: {new Date(removeCollaboratorConfirm.deadline).toLocaleDateString()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => setRemoveCollaboratorConfirm(null)}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              const currentCollaborators = formData?.collaborators || currentTask?.collaborators || [];
              const updatedCollaborators = currentCollaborators.filter((c, idx) => {
                if (removeCollaboratorConfirm._id) {
                  return c._id !== removeCollaboratorConfirm._id;
                }
                // Use the index from the stored collaborator
                return idx !== removeCollaboratorConfirm.index;
              });
              updateField('collaborators', updatedCollaborators);
              setRemoveCollaboratorConfirm(null);
              showSuccess('Team member removed from task');
            }}
            color="error"
            variant="contained"
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Content Type Menu */}
      <Menu
        anchorEl={contentTypeAnchor}
        open={Boolean(contentTypeAnchor)}
        onClose={() => setContentTypeAnchor(null)}
        PaperProps={{ sx: { width: 180 } }}
      >
        {CONTENT_TYPES.map(type => (
          <MenuItem
            key={type.value}
            selected={formData.contentType === type.value}
            onClick={() => {
              updateField('contentType', type.value);
              // Update category if needed
              const categories = getDynamicCategoriesForType(type.value);
              if (categories.length && !categories.some(c => c.value === formData.category)) {
                updateField('category', categories[0].value);
              }
              setContentTypeAnchor(null);
            }}
            sx={{ fontSize: '0.8125rem' }}
          >
            {type.label}
          </MenuItem>
        ))}
      </Menu>
      
      {/* Category Menu */}
      <Menu
        anchorEl={categoryAnchor}
        open={Boolean(categoryAnchor)}
        onClose={() => setCategoryAnchor(null)}
        PaperProps={{ sx: { width: 160 } }}
      >
        {getDynamicCategoriesForType(formData.contentType || 'post').map(cat => (
          <MenuItem
            key={cat.value}
            selected={formData.category === cat.value}
            onClick={() => {
              updateField('category', cat.value);
              setCategoryAnchor(null);
            }}
            sx={{ fontSize: '0.8125rem' }}
          >
            {cat.label}
          </MenuItem>
        ))}
      </Menu>
      
      {/* Language Menu */}
      <Menu
        anchorEl={languageAnchor}
        open={Boolean(languageAnchor)}
        onClose={() => setLanguageAnchor(null)}
        PaperProps={{ sx: { width: 140 } }}
      >
        {LANGUAGE_OPTIONS.map(lang => (
          <MenuItem
            key={lang.value}
            selected={formData.language === lang.value}
            onClick={() => {
              updateField('language', lang.value);
              setLanguageAnchor(null);
            }}
            sx={{ fontSize: '0.8125rem' }}
          >
            {lang.label}
          </MenuItem>
        ))}
      </Menu>
      
      {/* Hashtags Popover */}
      <Popover
        open={Boolean(hashtagsAnchor)}
        anchorEl={hashtagsAnchor}
        onClose={() => setHashtagsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 250 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Hashtags
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {formData.hashtags?.map((tag, index) => (
              <Chip
                key={index}
                label={`#${tag}`}
                size="small"
                onDelete={() => {
                  const newTags = formData.hashtags.filter((_, i) => i !== index);
                  updateField('hashtags', newTags);
                }}
                sx={{ height: 22, fontSize: '0.75rem' }}
              />
            ))}
          </Box>
          <TextField
            size="small"
            fullWidth
            placeholder="Add hashtag..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.value) {
                const tag = e.target.value.replace('#', '').trim();
                if (tag && !formData.hashtags?.includes(tag)) {
                  updateField('hashtags', [...(formData.hashtags || []), tag]);
                  e.target.value = '';
                }
              }
            }}
            sx={{ 
              '& .MuiInputBase-root': { 
                fontSize: '0.8125rem',
                height: 28
              }
            }}
          />
        </Box>
      </Popover>
      
      {/* Platforms Popover */}
      <Popover
        open={Boolean(platformsAnchor)}
        anchorEl={platformsAnchor}
        onClose={() => setPlatformsAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Select Platforms
          </Typography>
          <PlatformSelector
            selectedPlatforms={formData.platforms || []}
            onChange={(newPlatforms) => {
              updateField('platforms', newPlatforms);
            }}
            disabled={false}
          />
        </Box>
      </Popover>
      
      {/* Stage Menu */}
      <Menu
        anchorEl={stageAnchor}
        open={Boolean(stageAnchor)}
        onClose={() => setStageAnchor(null)}
        PaperProps={{ sx: { width: 150 } }}
      >
        {CONTENT_STAGES.map(stage => (
          <MenuItem
            key={stage.value}
            selected={formData.contentStage === stage.value}
            onClick={() => {
              updateField('contentStage', stage.value);
              setStageAnchor(null);
            }}
            sx={{ fontSize: '0.8125rem' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: {
                  todo: theme.palette.grey[500],
                  idea: theme.palette.info.main,
                  reference: theme.palette.secondary.main,
                  creative: theme.palette.primary.main,
                  posting: theme.palette.warning.main,
                  completed: theme.palette.success.main
                }[stage.value] || theme.palette.grey[500]
              }} />
              {stage.label}
            </Box>
          </MenuItem>
        ))}
      </Menu>
      
      {/* Time Tracker Dialog */}
      <Dialog
        open={timeTrackerDialog}
        onClose={() => setTimeTrackerDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimeIcon color="secondary" />
              <Typography variant="h6">Time Tracking</Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setTimeTrackerDialog(false)}
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <TaskTimeTracker 
            task={currentTask}
            onTimeUpdate={async () => {
              // Force reload task to get updated time entries
              try {
                const response = await taskService.getTask(taskId);
                const updatedTask = response.data?.task || response.task;
                if (updatedTask) {
                  // Update the current task in the store using setState
                  useTaskStore.setState((state) => ({ 
                    currentTask: {
                      ...updatedTask,
                      content: state.currentTask?.content || updatedTask.content
                    }
                  }));
                }
              } catch (error) {
                console.error('Failed to refresh task:', error);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimeTrackerDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
    </Box>
  );
};

export default MinimalTaskDetails;